import { useEffect, useImperativeHandle, useState, type Ref } from "react";

import type {
  ArchivoDeAntecedente,
  CategoriaDocumento,
  DocumentoDelCatalogo,
  Obligatoriedad,
} from "@/lib/informes";
import { separarPorValidez, textoDeFallos, type FalloDeArchivo } from "@/lib/subirEnSecuencia";
import { cn } from "@/lib/utils";

import { motivoDeRechazoDelAntecedente } from "./ArchivosDelDocumento";
import { BarraDeGuardado } from "./BarraDeGuardado";
import { ChipDelCuadro } from "./ChipDelCuadro";
import {
  obligatoriedadDe,
  type DefinicionDeDocumentos,
} from "./definicionDeDocumentos";
import {
  FILTROS,
  archivosSinClasificar,
  contarPorFiltro,
  filasDeLosDocumentos,
  filtrarFilas,
  resumenDeObligatorios,
  type FiltroDeDocumentos,
} from "./documentosDeLaPestana";
import { ArchivosSinClasificar } from "./ArchivosSinClasificar";
import { FilaDelDocumento } from "./FilaDelDocumento";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import type { Clasificacion } from "./SelectorDeClasificacion";

/**
 * Los archivos de la pestaña, y qué se puede hacer con ellos.
 *
 * LAS DOS PESTAÑAS ADJUNTAN, y esto antes era una unión discriminada porque no
 * era así: la de LEGALES tenía un modo de solo lectura —esos documentos los
 * entregaba el cliente por su portal y el equipo solo los miraba y los bajaba—
 * frente a la de HISTÓRICOS, que son los antecedentes que el equipo consigue
 * por su cuenta. Hoy el equipo también carga legales, porque el cliente se los
 * manda por fuera del portal (correo, mensajería, en mano) tan seguido como por
 * él, y sin esta puerta esos papeles se quedaban afuera del expediente.
 *
 * Lo que el portal aportaba sigue en pie y NO se diluyó: cada archivo dice de
 * dónde vino (`origen`), y lo que mandó el cliente no se puede quitar desde
 * acá. Esa distinción vive ahora en la FILA —ver
 * {@link ArchivosDelDocumento}— y no en el modo de la pestaña.
 */
export interface ArchivosDeLaPestana {
  /** Los archivos del informe ENTERO; la pestaña filtra los suyos. */
  archivos: ArchivoDeAntecedente[];
  urlDeDescarga: (archivoId: number) => string;
  /** El `documento_tipo_id` con una subida o un borrado en curso, o `null`. */
  ocupadoEn: number | null;
  /**
   * Los archivos que pasaron la validación del navegador, en orden. Quien
   * llama los sube de a uno y devuelve los que el SERVIDOR rechazó, para
   * decirlos por nombre junto a los del navegador; sin retorno, no hay fallos
   * que mostrar.
   */
  onAdjuntar: (
    documentoTipoId: number,
    archivos: File[],
  ) => void | Promise<FalloDeArchivo[]>;
  onQuitar: (archivoId: number) => void;
  /**
   * Corrige la fecha de EMISIÓN de un archivo (rebanada 2 de
   * `odd/tasks/fecha-del-documento-y-tasacion-mas-reciente.md`). `fecha` en
   * `null` vacía la corrección.
   */
  onCorregirFecha: (archivoId: number, fecha: string | null) => void;
  /**
   * Escribe la nota del tasador sobre un archivo
   * (`odd/tasks/notas-por-archivo.md`). `nota` en `null` la vacía. Se guarda
   * en el acto, así que NO cuenta como cambio sin guardar de la pestaña.
   */
  onAnotar: (archivoId: number, nota: string | null) => void;
  /**
   * "Reintentar" un archivo trabado o fallido
   * (`odd/tasks/transcripcion-en-limbo.md`). Se ofrece solo cuando el
   * servidor marca `contexto_reintentable`, para cualquier origen.
   */
  onReprocesar: (archivoId: number) => void;
  /**
   * Clasifica un archivo en un tipo de documento, o con `null` lo devuelve a
   * "sin clasificar". Resuelve `true` si el servidor lo aceptó: con `false`
   * (un 422, que la pantalla ya mostró) el selector queda abierto.
   */
  onClasificar: (archivoId: number, documentoTipoId: number | null) => Promise<boolean>;
  /** El id del archivo con un reproceso EN VUELO, o `null`. */
  archivoReprocesando: number | null;
}

/** Lo que la pantalla puede pedirle a la pestaña desde el aviso de salida. */
export interface PestanaDeDocumentosHandle {
  /** Guarda, igual que apretar el botón de la barra. */
  guardar: () => void;
  /** Toma lo que hay en pantalla como "lo guardado" y apaga el aviso. */
  marcarGuardado: () => void;
}

interface PestanaDeDocumentosProps {
  categoria: CategoriaDocumento;
  documentos: DocumentoDelCatalogo[];
  /**
   * El catálogo de LAS DOS categorías. La pestaña solo lista el suyo, pero el
   * selector de clasificación ofrece ambos (ver {@link SelectorDeClasificacion}).
   */
  catalogos: Clasificacion["catalogos"];
  definicionInicial: DefinicionDeDocumentos;
  notasIniciales: string;
  guardando: boolean;
  adjuntos: ArchivosDeLaPestana;
  /**
   * Si el informe ya está en un estado que admite el resto del expediente (de
   * la aceptación en adelante). Solo cambia una frase del resumen de legales:
   * mientras no lo esté, avisa que las demás pestañas esperan al cliente.
   */
  expedienteHabilitado?: boolean;
  ref?: Ref<PestanaDeDocumentosHandle>;
  /** Avisa si hay cambios sin guardar en esta pestaña. */
  onCambios?: (hayCambios: boolean) => void;
  onGuardar: (
    definicion: { documento_tipo_id: number; obligatoriedad: Obligatoriedad }[],
    notas: string,
  ) => void;
}

/**
 * Cuántos documentos tienen una obligatoriedad distinta de la guardada.
 *
 * Se compara YA RESUELTO: la ausencia de clave es Obligatorio, así que `{}` y
 * `{ 11: "obligatorio" }` describen exactamente lo mismo y no son un cambio.
 */
function cuantasObligatoriedadesCambiaron(
  documentos: DocumentoDelCatalogo[],
  actual: DefinicionDeDocumentos,
  guardada: DefinicionDeDocumentos,
): number {
  return documentos.filter((d) => obligatoriedadDe(actual, d.id) !== obligatoriedadDe(guardada, d.id))
    .length;
}

/**
 * Una pestaña de antecedentes requeridos (RF-E5): un resumen de avance con
 * filtros, la lista del catálogo con su conmutador Obligatorio/Opcional/No
 * aplica y los archivos recibidos de cada documento.
 *
 * TODO DOCUMENTO ES OBLIGATORIO POR DEFECTO (decisión de producto del
 * 29-09-2026): el conmutador siempre tiene una opción marcada, y no existe el
 * estado "Sin definir" ni la manera de volver a él. Elegir otra opción es la
 * excepción que la persona decide; tocar la ya marcada no hace nada.
 *
 * EL RESUMEN MIDE LO QUE LLEGÓ, no lo que se definió: "X de N obligatorios con
 * archivo". Es el mismo conteo que pinta la tarjeta de legales en la barra de
 * pestañas (`estadoDeLaPestana`), y las dos salen de la misma regla.
 *
 * SE GUARDA DESDE LA BARRA DEL PIE, y lo guardado es la obligatoriedad y las
 * notas: los archivos se suben solos al adjuntarlos, así que no cuentan como
 * cambios sin guardar —cuando ese aviso aparecería, ya están en el servidor—.
 *
 * El MISMO componente sirve para legales e históricos porque son la misma
 * pantalla sobre distinta categoría — duplicarla garantizaría que se
 * desincronicen.
 *
 * `categoria` viaja al backend en cada guardado y no es decorativa: el borrado
 * de "lo que ya no viene" se acota con ella. Sin ese alcance, guardar esta
 * pestaña borraría la definición de la otra.
 */
export function PestanaDeDocumentos({
  categoria,
  documentos,
  catalogos,
  definicionInicial,
  notasIniciales,
  guardando,
  adjuntos,
  expedienteHabilitado = true,
  ref,
  onCambios,
  onGuardar,
}: PestanaDeDocumentosProps) {
  const [definicion, setDefinicion] = useState<DefinicionDeDocumentos>(definicionInicial);
  const [notas, setNotas] = useState(notasIniciales);
  const [filtro, setFiltro] = useState<FiltroDeDocumentos>("todos");
  /* Los documentos con sus archivos desplegados. Arrancan todos plegados. */
  const [abiertos, setAbiertos] = useState<Set<number>>(() => new Set());

  /* Los rechazos del NAVEGADOR (tipo y peso), por documento. No son los del
     servidor —esos llegan como toast desde la pantalla, que es quien llama a la
     API—: son los que evitan mandar 32 MB por la red para enterarse después. */
  const [rechazos, setRechazos] = useState<Record<number, string>>({});

  async function adjuntar(documentoTipoId: number, archivos: File[]) {
    // Un archivo rechazado NO frena el lote: se aparta, se dice por nombre y
    // los demás suben.
    const { validos, rechazados } = separarPorValidez(archivos, motivoDeRechazoDelAntecedente);

    setRechazos((actuales) => ({ ...actuales, [documentoTipoId]: textoDeFallos(rechazados) }));

    if (validos.length === 0) return;

    const delServidor = (await adjuntos.onAdjuntar(documentoTipoId, validos)) ?? [];

    if (delServidor.length > 0) {
      setRechazos((actuales) => ({
        ...actuales,
        [documentoTipoId]: textoDeFallos([...rechazados, ...delServidor]),
      }));
    }

    // Lo recién subido se ve: abrir la fila es la confirmación de que llegó.
    setAbiertos((actuales) => new Set(actuales).add(documentoTipoId));
  }

  /* La referencia de "lo guardado" es estado y no las props: después de un
     guardado exitoso la base tiene que moverse a lo que quedó en pantalla, y
     las props recién traen ese dato cuando vuelve el refetch. */
  const [base, setBase] = useState({ definicion: definicionInicial, notas: notasIniciales });

  const cambios =
    cuantasObligatoriedadesCambiaron(documentos, definicion, base.definicion)
    + (notas !== base.notas ? 1 : 0);
  const hayCambios = cambios > 0;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: () => setBase({ definicion, notas }),
  }));

  const clasificacion: Clasificacion = {
    catalogos,
    categoriaActual: categoria,
    onClasificar: adjuntos.onClasificar,
  };
  const sinClasificar = archivosSinClasificar(adjuntos.archivos, categoria);

  const filas = filasDeLosDocumentos(documentos, definicion, adjuntos.archivos);
  const resumen = resumenDeObligatorios(filas);
  const conteo = contarPorFiltro(filas);
  const visibles = filtrarFilas(filas, filtro);

  /* "Ver todos" y "Contraer" son el mismo botón: dice lo que va a hacer según
     haya algo abierto entre lo que se ve. */
  const hayAlgoAbierto = visibles.some((f) => f.archivos.length > 0 && abiertos.has(f.documento.id));

  function alternarTodos() {
    setAbiertos(
      hayAlgoAbierto
        ? new Set()
        : new Set(visibles.filter((f) => f.archivos.length > 0).map((f) => f.documento.id)),
    );
  }

  function alternar(id: number) {
    setAbiertos((actuales) => {
      const siguiente = new Set(actuales);
      if (!siguiente.delete(id)) siguiente.add(id);
      return siguiente;
    });
  }

  function elegir(id: number, valor: Obligatoriedad) {
    setDefinicion((actual) => ({ ...actual, [id]: valor }));
  }

  /* Viaja TODA la pestaña con su valor resuelto, Obligatorio incluido: guardar
     es explícito, y lo que no viaja el servidor lo borra (vuelve al valor por
     defecto, que es el mismo Obligatorio). */
  function guardar() {
    onGuardar(
      documentos.map((d) => ({
        documento_tipo_id: d.id,
        obligatoriedad: obligatoriedadDe(definicion, d.id),
      })),
      notas,
    );
  }

  function descartarLosCambios() {
    setDefinicion(base.definicion);
    setNotas(base.notas);
  }

  const obligatorios = categoria === "legal" ? "obligatorios" : "obligatorias";
  const rotuloDelAvance = `${obligatorios.charAt(0).toUpperCase()}${obligatorios.slice(1)} con archivo`;

  return (
    <div className="flex flex-col gap-3.5">
      {/* El título de la pestaña lo dibuja `EncabezadoDePestana`; acá empieza el
          resumen de avance. */}
      <section className="bg-card flex flex-col gap-3 rounded-xl border-[1.5px] px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <div className="min-w-55 flex-1">
            <div className="flex items-baseline gap-2">
              <span
                className={cn(
                  "font-mono text-[1.375rem] font-bold tracking-[-0.02em]",
                  resumen.completo ? "text-success-soft-foreground" : "text-ink",
                )}
              >
                {resumen.conArchivo}
              </span>
              <span className="text-ink-body text-[0.8125rem] font-bold">
                de {resumen.obligatorios} {obligatorios} con archivo
              </span>
            </div>
            <div
              role="progressbar"
              aria-label={rotuloDelAvance}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(resumen.porcentaje)}
              aria-valuetext={`${resumen.conArchivo} de ${resumen.obligatorios}`}
              className="bg-muted mt-2 h-1.5 max-w-105 overflow-hidden rounded-[0.1875rem]"
            >
              <span
                className="bg-success-dot block h-full rounded-[0.1875rem]"
                style={{ width: `${resumen.porcentaje}%` }}
              />
            </div>
          </div>

          <p className="text-ink-muted min-w-70 flex-1 text-[0.71875rem] font-semibold text-pretty">
            {categoria === "legal"
              ? "Todos los documentos parten como obligatorios. "
              : "Todas las categorías parten como obligatorias. "}
            Marca <span className="text-ink font-bold">Opcional</span> o{" "}
            <span className="text-ink font-bold">No aplica</span> según el predio.
            {categoria === "legal" && !expedienteHabilitado
              ? " El resto de las pestañas se habilita cuando el cliente aprueba la cotización."
              : null}
          </p>
        </div>

        {/* LOS CINCO FILTROS SE VEN SIEMPRE, también con el conteo en cero: un
            control que se esconde cuando no tiene nada no se entiende, y quien
            lo busca no sabe si no existe o si no hay con qué usarlo. Tocar el
            filtro activo lo suelta. */}
        <div className="flex flex-wrap items-center gap-1.5 border-t pt-3">
          {FILTROS.map(({ clave, rotulo, punto }) => (
            <ChipDelCuadro
              key={clave}
              activo={filtro === clave}
              rotulo={rotulo}
              cantidad={conteo[clave]}
              punto={
                punto ? (
                  <span
                    aria-hidden="true"
                    className={cn(
                      "size-[0.4375rem] rounded-full border-[1.5px]",
                      punto === "ok" && "border-success-dot bg-success-dot",
                      punto === "falta" && "border-warning-dot bg-warning-dot",
                      punto === "vacio" && "border-ink-ghost border-dashed bg-transparent",
                    )}
                  />
                ) : undefined
              }
              onClick={() => setFiltro(filtro === clave ? "todos" : clave)}
            />
          ))}

          <button
            type="button"
            onClick={alternarTodos}
            className="text-ink-body hover:text-ink ml-auto text-xs font-bold"
          >
            {hayAlgoAbierto ? "Contraer archivos" : "Ver todos los archivos"}
          </button>
        </div>
      </section>

      {/* LO QUE EL CLIENTE MANDÓ SIN SABER QUÉ ERA va ARRIBA de la lista y se
          esconde cuando no queda nada: es trabajo pendiente, y la IA no lo lee
          hasta que se clasifica. */}
      <ArchivosSinClasificar
        archivos={sinClasificar}
        urlDeDescarga={adjuntos.urlDeDescarga}
        clasificacion={clasificacion}
      />

      <section className="bg-card overflow-hidden rounded-xl border-[1.5px]">
        {documentos.length === 0 ? (
          <p className="text-ink-muted p-5 text-sm">
            No hay documentos en esta categoría del catálogo.
          </p>
        ) : visibles.length === 0 ? (
          <p className="text-ink-muted px-5 py-5 text-center text-[0.78125rem] font-semibold">
            No hay documentos con este filtro.
          </p>
        ) : (
          visibles.map((fila) => (
            <FilaDelDocumento
              key={fila.documento.id}
              fila={fila}
              abierta={abiertos.has(fila.documento.id)}
              onAlternar={() => alternar(fila.documento.id)}
              onElegir={(valor) => elegir(fila.documento.id, valor)}
              urlDeDescarga={adjuntos.urlDeDescarga}
              ocupado={adjuntos.ocupadoEn === fila.documento.id}
              error={rechazos[fila.documento.id] || null}
              onAdjuntar={(archivos) => void adjuntar(fila.documento.id, archivos)}
              onQuitar={(archivoId) => adjuntos.onQuitar(archivoId)}
              onCorregirFecha={(archivoId, fecha) => adjuntos.onCorregirFecha(archivoId, fecha)}
              onAnotar={(archivoId, nota) => adjuntos.onAnotar(archivoId, nota)}
              onReprocesar={(archivoId) => adjuntos.onReprocesar(archivoId)}
              archivoReprocesando={adjuntos.archivoReprocesando}
              clasificacion={clasificacion}
            />
          ))
        )}
      </section>

      <NotasDeLaPestana
        id={`notas-${categoria}`}
        value={notas}
        onChange={setNotas}
        disabled={guardando}
      />

      {/* LA BARRA VA AL PIE de la pestaña, como en Construcciones. Cuenta lo que
          se guarda con ella —obligatoriedades y notas—; los archivos no entran
          porque ya están subidos. */}
      <BarraDeGuardado
        cambios={cambios}
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Los archivos se guardan solos al adjuntarlos."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />
    </div>
  );
}
